import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, IconButton, Tooltip, Button, TextField, InputAdornment,
  Chip, Checkbox, Select, MenuItem, CircularProgress, Collapse,
} from '@material-ui/core';
import AssignmentIcon from '@material-ui/icons/Assignment';
import DragHandleIcon from '@material-ui/icons/DragHandle';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SearchIcon from '@material-ui/icons/Search';
import DirectionsIcon from '@material-ui/icons/Directions';
import RoomIcon from '@material-ui/icons/Room';
import FlagIcon from '@material-ui/icons/Flag';
import PersonIcon from '@material-ui/icons/Person';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import SendIcon from '@material-ui/icons/Send';
import DeleteIcon from '@material-ui/icons/Delete';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import RefreshIcon from '@material-ui/icons/Refresh';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import ClearIcon from '@material-ui/icons/Clear';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import EditIcon from '@material-ui/icons/Edit';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import AddIcon from '@material-ui/icons/Add';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import { openLiveWindow } from '../../utils/liveWindow';
import RoutePlaybackDialog from './RoutePlaybackDialog';
import AddOTToRouteDialog from './AddOTToRouteDialog';
import { AuthContext } from '../../App';
import { selectUI, setActiveView, navigateTo, setSidebarVisible, setDisplayedSavedRoutes, setSidebarTab, setShowDetails } from '../../store/slices/uiSlice';
import {
  deleteRouteFromAPI, saveRouteToAPI, sendRouteToVehicle, fetchRouteDetail,
  setReplanningRouteId, reopenRoute, duplicateRoute,
} from '../../store/slices/routesSlice';
import { addToast } from '../../store/slices/notificationsSlice';
import { exportRoutePdf, exportDriverPdf } from '../../utils/exportPdf';
import DriverPdfPreviewDialog from '../common/DriverPdfPreviewDialog';
import RoutePreviewMap from './RoutePreviewMap';
import LiveRouteMonitor from './LiveRouteMonitor';

const API_BASE = '/planner-api';

const STATUS_CFG = {
  draft:       { label: 'Rascunho',  color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
  planned:     { label: 'Planeada',  color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
  in_progress: { label: 'Em Trânsito', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  completed:   { label: 'Concluída', color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  cancelled:   { label: 'Cancelada', color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
};

// Sub-fase "regresso" — a rota continua em `in_progress` (compat com
// clientes antigos) mas o operador vê este badge azul quando todas as
// entregas estão feitas e o motorista está só a voltar ao ponto final.
const RETURNING_CFG = { label: '🏠 Em regresso', color: '#0ea5e9', bg: 'rgba(14,165,233,0.12)' };

function getRouteStatusCfg(r) {
  if (r?.status === 'in_progress' && r?.routeData?.phase === 'returning') return RETURNING_CFG;
  return STATUS_CFG[r?.status] || STATUS_CFG.draft;
}

const PERIOD_OPTIONS = [
  { key: 'all',   label: 'Todos os períodos' },
  { key: 'today', label: 'Hoje' },
  { key: 'week',  label: 'Última semana' },
  { key: 'month', label: 'Último mês' },
  { key: 'year',  label: 'Último ano' },
];

const GROUP_OPTIONS = [
  { key: 'none',   label: 'Sem agrupamento' },
  { key: 'driver', label: 'Por motorista' },
  { key: 'status', label: 'Por status' },
  { key: 'date',   label: 'Por data' },
];

const STATUS_OPTIONS = ['draft', 'planned', 'in_progress', 'completed'];
const PAGE_LIMIT_OPTIONS = [25, 50, 100];
const PAGE_LIMIT_DEFAULT = 25;

const useStyles = makeStyles(() => ({
  root: {
    width: '100%', height: '100%',
    background: '#f1f5f9',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 24px', background: '#fff',
    borderBottom: '1px solid #e2e8f0',
    flexShrink: 0,
  },
  titleIcon: {
    width: 36, height: 36, borderRadius: 10,
    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
  },
  titleBlock: { display: 'flex', alignItems: 'center', gap: 10, flex: 1 },
  title: {
    fontSize: '1.05rem', fontWeight: 700, color: '#1e293b',
  },
  totalChip: {
    height: 22, fontSize: '0.68rem', fontWeight: 700,
    background: '#eef2ff', color: '#4338ca',
  },
  headerActions: { display: 'flex', alignItems: 'center', gap: 6 },
  filterBar: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
    padding: '10px 24px', background: '#fff',
    borderBottom: '1px solid #f1f5f9',
    flexShrink: 0,
  },
  searchField: {
    flex: 1, minWidth: 220, maxWidth: 360,
    '& .MuiOutlinedInput-root': {
      borderRadius: 10, background: '#f8fafc',
      fontSize: '0.78rem', height: 36,
    },
    '& .MuiOutlinedInput-input': { padding: '8px 12px' },
  },
  periodSelect: {
    minWidth: 170, fontSize: '0.76rem', height: 36,
    background: '#f8fafc', borderRadius: 10,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.08)' },
  },
  groupSelect: {
    minWidth: 160, fontSize: '0.76rem', height: 36,
    background: '#f8fafc', borderRadius: 10,
  },
  statusRow: {
    display: 'flex', gap: 5, alignItems: 'center',
  },
  statusChip: {
    height: 24, fontSize: '0.64rem', fontWeight: 700,
    cursor: 'pointer', transition: 'all 0.15s',
    border: '1px solid',
  },
  splitContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
    position: 'relative',
  },
  topPane: {
    overflow: 'auto',
    padding: '16px 24px 20px',
    minHeight: 0,
  },
  resizeHandle: {
    height: 10,
    flexShrink: 0,
    cursor: 'ns-resize',
    position: 'relative',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'transparent',
    zIndex: 4,
    '&:hover $resizeGrip': {
      background: '#6366f1',
      width: 80,
    },
  },
  resizeGrip: {
    width: 48,
    height: 4,
    borderRadius: 2,
    background: 'rgba(99,102,241,0.35)',
    transition: 'all 0.15s',
  },
  bottomPane: {
    minHeight: 0,
    position: 'relative',
    background: 'linear-gradient(180deg, #e2e8f0 0%, #cbd5e1 100%)',
    padding: '0 16px 16px 16px',
    display: 'flex',
    flexDirection: 'column',
  },
  mapFloatingCard: {
    flex: 1,
    minHeight: 0,
    borderRadius: 16,
    overflow: 'hidden',
    background: '#fff',
    border: '1px solid rgba(255,255,255,0.6)',
    boxShadow: '0 20px 50px -12px rgba(15,23,42,0.35), 0 8px 20px -6px rgba(99,102,241,0.18), 0 0 0 1px rgba(255,255,255,0.9) inset',
    position: 'relative',
  },
  orderBanner: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 20px',
    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 60%, #4338ca 100%)',
    color: '#fff',
    borderBottom: '1px solid rgba(255,255,255,0.12)',
    flexShrink: 0,
    position: 'relative',
    overflow: 'hidden',
  },
  orderBannerIcon: {
    width: 38, height: 38, borderRadius: 10,
    background: 'rgba(255,255,255,0.18)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.25)',
  },
  orderBannerContent: {
    flex: 1, minWidth: 0,
    display: 'flex', flexDirection: 'column', gap: 2,
  },
  orderBannerLabel: {
    fontSize: '0.58rem', fontWeight: 700,
    color: 'rgba(255,255,255,0.75)',
    textTransform: 'uppercase', letterSpacing: '0.08em',
  },
  orderBannerTitle: {
    fontSize: '0.92rem', fontWeight: 700,
    color: '#fff',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  orderBannerMeta: {
    display: 'flex', alignItems: 'center', gap: 14,
    fontSize: '0.68rem', fontWeight: 500,
    color: 'rgba(255,255,255,0.88)',
  },
  orderBannerChip: {
    height: 20, fontSize: '0.6rem', fontWeight: 700,
    background: 'rgba(255,255,255,0.22)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.35)',
  },
  content: {
    flex: 1, overflow: 'auto', padding: '16px 24px',
  },
  bulkBar: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 16px', marginBottom: 12,
    background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
    border: '1px solid #c7d2fe',
    borderRadius: 12,
    animation: '$slideDown 0.2s ease-out',
    // Sticky at the top of the scroll container so it stays visible
    // while the operator scrolls through the list below
    position: 'sticky',
    top: 0,
    zIndex: 10,
    boxShadow: '0 4px 12px -2px rgba(99,102,241,0.15)',
  },
  '@keyframes slideDown': {
    '0%': { opacity: 0, transform: 'translateY(-10px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
  },
  bulkText: {
    fontSize: '0.78rem', fontWeight: 700, color: '#4338ca',
    flex: 1,
  },
  bulkBtn: {
    fontSize: '0.7rem', fontWeight: 600, textTransform: 'none',
    padding: '4px 12px', borderRadius: 8, minWidth: 'auto',
  },
  tableCard: {
    background: '#fff', borderRadius: 12,
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
  },
  tableHeaderRow: {
    display: 'grid',
    gridTemplateColumns: '30px 2fr 90px 90px 90px 110px 2.3fr 70px 70px 90px 90px',
    gap: 8,
    padding: '6px 12px',
    background: '#f8fafc',
    borderBottom: '2px solid #e2e8f0',
    position: 'sticky',
    top: 0,
    zIndex: 9,
  },
  th: {
    fontSize: '0.56rem', fontWeight: 700, color: '#64748b',
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '30px 2fr 90px 90px 90px 110px 2.3fr 70px 70px 90px 90px',
    gap: 8,
    padding: '4px 12px',
    borderBottom: '1px solid #f1f5f9',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background 0.12s',
    borderLeft: '3px solid transparent',
    minHeight: 34,
    '&:hover': { background: '#f8fafc' },
  },
  tableRowSelected: {
    background: 'rgba(99,102,241,0.06) !important',
  },
  tableRowPreviewed: {
    background: 'linear-gradient(90deg, rgba(99,102,241,0.14), rgba(99,102,241,0.04)) !important',
    borderLeft: '3px solid #6366f1 !important',
    boxShadow: 'inset 0 0 0 1px rgba(99,102,241,0.18), 0 2px 8px -2px rgba(99,102,241,0.25)',
    position: 'relative',
    '& $routeName': {
      color: '#4338ca !important',
    },
  },
  cellText: {
    fontSize: '0.68rem', color: '#1e293b',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  cellSub: {
    fontSize: '0.56rem', color: '#94a3b8',
  },
  routeName: {
    fontSize: '0.72rem', fontWeight: 700, color: '#0f172a',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    lineHeight: 1.25,
  },
  odRow: {
    display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0,
  },
  odLine: {
    display: 'flex', alignItems: 'center', gap: 3,
    fontSize: '0.62rem', color: '#475569',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    lineHeight: 1.2,
  },
  statusBadge: {
    height: 18, fontSize: '0.54rem', fontWeight: 700,
    border: '1px solid',
  },
  // Sub-row that surfaces under any in-progress route. Holds progress bar,
  // elapsed/remaining times, GPS indicator dot and the "Ver em tempo real"
  // CTA. Sits inside the main row's grid via `gridColumn: 1 / -1` so the
  // existing column layout is preserved.
  liveBar: {
    gridColumn: '1 / -1',
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '6px 4px 8px',
    borderTop: '1px dashed rgba(245,158,11,0.25)',
    marginTop: 2,
  },
  liveProgressTrack: {
    flex: 1, minWidth: 120, height: 6, borderRadius: 999,
    background: '#e2e8f0', overflow: 'hidden',
  },
  liveProgressFill: {
    height: '100%', borderRadius: 999,
    background: 'linear-gradient(90deg, #22c55e, #16a34a)',
    transition: 'width 0.4s ease',
  },
  liveStat: {
    fontSize: '0.6rem', fontWeight: 700, color: '#0f172a',
    display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
  },
  liveStatLabel: {
    fontSize: '0.52rem', color: '#94a3b8', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  liveDot: {
    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
    boxShadow: '0 0 0 2px rgba(255,255,255,0.6)',
  },
  liveCta: {
    fontSize: '0.65rem', fontWeight: 700, textTransform: 'none',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#fff', borderRadius: 8, padding: '4px 12px', minHeight: 0,
    boxShadow: '0 2px 6px rgba(59,130,246,0.25)',
    '&:hover': {
      background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
    },
  },
  groupHeader: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 14px',
    background: '#eef2ff',
    borderBottom: '1px solid #c7d2fe',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': { background: '#e0e7ff' },
  },
  groupLabel: {
    fontSize: '0.78rem', fontWeight: 700, color: '#4338ca', flex: 1,
  },
  groupCount: {
    fontSize: '0.65rem', fontWeight: 700, color: '#6366f1',
    background: '#fff', padding: '2px 8px', borderRadius: 10,
  },
  emptyState: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: 48, gap: 10, color: '#94a3b8',
  },
  loadingBox: {
    display: 'flex', justifyContent: 'center', padding: 60,
  },
  pagination: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    padding: '14px 0', background: '#fff', borderTop: '1px solid #e2e8f0',
    flexShrink: 0,
  },
  pageBtn: {
    padding: 6, border: '1px solid #e2e8f0', background: '#f8fafc',
    '&:disabled': { opacity: 0.4 },
  },
  pageInfo: {
    fontSize: '0.74rem', color: '#64748b', fontWeight: 600,
  },
}));

/* ─────────────────────────────────────────────────────────── */
/* Helpers                                                     */
/* ─────────────────────────────────────────────────────────── */

function periodToDates(period) {
  if (period === 'all') return {};
  const now = new Date();
  const to = now.toISOString();
  const from = new Date(now);
  if (period === 'today') from.setHours(0, 0, 0, 0);
  else if (period === 'week') from.setDate(now.getDate() - 7);
  else if (period === 'month') from.setMonth(now.getMonth() - 1);
  else if (period === 'year') from.setFullYear(now.getFullYear() - 1);
  return { dateFrom: from.toISOString(), dateTo: to };
}

function formatKm(m) {
  if (!m) return '—';
  return `${(m / 1000).toFixed(0)} km`;
}

function formatDuration(sec) {
  if (!sec) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  if (h > 0) return `${h}h${m > 0 ? ` ${m}m` : ''}`;
  return `${m}m`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-PT', {
    day: '2-digit', month: '2-digit', year: '2-digit',
  });
}

function groupRoutes(routes, groupBy) {
  if (groupBy === 'none') return [{ key: 'all', label: null, items: routes }];
  const map = new Map();
  routes.forEach(r => {
    let key, label;
    if (groupBy === 'driver') {
      key = r.driver?.id || 'none';
      label = r.driver?.name || 'Sem motorista';
    } else if (groupBy === 'status') {
      key = r.status || 'draft';
      label = STATUS_CFG[key]?.label || key;
    } else if (groupBy === 'date') {
      const d = r.createdAt ? new Date(r.createdAt) : null;
      key = d ? d.toLocaleDateString('pt-PT') : 'sem-data';
      label = d ? d.toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' }) : 'Sem data';
    }
    if (!map.has(key)) map.set(key, { key, label, items: [] });
    map.get(key).items.push(r);
  });
  return Array.from(map.values());
}

/* ─────────────────────────────────────────────────────────── */
/* Main                                                        */
/* ─────────────────────────────────────────────────────────── */

// Props:
//   embedded       — render without the page-level header (host wraps it).
//   compact        — denser rows for high-density browsers (e.g. drawer in
//                    the planner). Hides the sub-row "live" details and
//                    reduces padding.
//   hideMapPreview — never show the bottom-pane map preview (overrides
//                    localStorage / the user toggle). Always true for
//                    embedded compact use.
//   onSelectRoute  — called with `{ id, dbId, ... }` when the operator
//                    clicks a row. When provided, replaces the default
//                    "open in monitor / preview" behaviour.
function RoutesView({ embedded = false, compact = false, hideMapPreview = false, onSelectRoute = null } = {}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token, clientSettings } = useContext(AuthContext);
  // "Usa App" deriva da config de Ordens (orderFlows[t].usesApp) — igual ao
  // Header e ao RouteDetails. Sem App, o botão "PDF Motorista" (pré-visualização
  // no telemóvel) esconde-se. Fail-open quando não há config.
  const usesDriverApp = (() => {
    const flows = clientSettings?.orderFlows;
    if (!flows || Object.keys(flows).length === 0) return true;
    return Object.values(flows).some(f => f?.usesApp !== false);
  })();
  const { lang, previousView } = useSelector(selectUI);
  // Drawer mode (planner): no row selection / bulk actions — those live in
  // Operações > Rotas. Here a click just loads the route into the planner.
  const selectionEnabled = !onSelectRoute;
  const gridOverride = selectionEnabled
    ? null
    : { gridTemplateColumns: '2fr 90px 90px 90px 110px 2.3fr 70px 70px 90px 90px' };
  // Live vehicle positions (Traccar via WS) — used to surface a "GPS live"
  // pulse next to in-progress routes so the operator instantly sees which
  // technicians are reporting position vs. which went dark.
  const vehicles = useSelector(s => s.vehicles?.list || []);
  const pt = lang === 'pt';

  // Re-render every 30s while there are in-progress routes so the
  // elapsed/remaining counters tick without needing a full refetch.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // Operator-selectable page size — persisted so a dispatcher who likes
  // 100 rows per screen doesn't have to re-pick it on every visit.
  const [pageSize, setPageSize] = useState(() => {
    const saved = parseInt(localStorage.getItem('routesLibrary.pageSize'));
    return PAGE_LIMIT_OPTIONS.includes(saved) ? saved : PAGE_LIMIT_DEFAULT;
  });
  useEffect(() => {
    localStorage.setItem('routesLibrary.pageSize', String(pageSize));
  }, [pageSize]);

  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState('month');
  const [statusFilter, setStatusFilter] = useState('');
  const [groupBy, setGroupBy] = useState('none');
  const [selected, setSelected] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  // Routes whose OT children are visible inline. Operator clicks the
  // chevron next to the route name to expand and see the list of orders
  // attached to the route — same parent/child reading the planner uses.
  const [expandedRoutes, setExpandedRoutes] = useState(new Set());
  const toggleExpand = useCallback((rid) => {
    setExpandedRoutes(prev => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid);
      else next.add(rid);
      return next;
    });
  }, []);

  // Preview state — full route detail of the currently highlighted row
  const [previewId, setPreviewId] = useState(null);
  const [previewRoute, setPreviewRoute] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Delete confirmation dialog state. `step` drives the look and copy:
  //   'initial'       — plain "are you sure"
  //   'linked'        — route has open OTs (assigned/in_transit), must `force`
  //   'linked-closed' — route has closed OTs (delivered/cancelled), must `purgeHistory`
  const [deleteDialog, setDeleteDialog] = useState(null);
  // Preview map visibility — persisted in localStorage so the operator keeps the choice
  // Live route monitor — full-screen split-view that takes over the panel
  // when the operator clicks "Ver em tempo real" on an in-progress row.
  // Null = list mode; routeId = monitor mode.
  const [liveMonitorRouteId, setLiveMonitorRouteId] = useState(null);
  // Route to play back as animation (replay of actual GPS trail). Stores
  // {id, name} so the dialog header can show the route name without an
  // extra fetch.
  const [playbackRoute, setPlaybackRoute] = useState(null);

  // Route receiving a new OT (FASE 1: só draft). Mesma estrutura do
  // playback — id+name+status para o diálogo poder mostrar o aviso
  // de in_progress quando aplicável.
  const [addOTRoute, setAddOTRoute] = useState(null);

  // External callers (e.g. the Orders screen) can open the live monitor for
  // a specific route by dispatching `openRouteLive` with `{ routeId }`.
  // Sempre via janela própria — mesma UX que o botão da listagem.
  useEffect(() => {
    function handler(e) {
      const id = Number(e?.detail?.routeId);
      if (Number.isFinite(id)) openLiveWindow(id, token);
    }
    window.addEventListener('openRouteLive', handler);
    return () => window.removeEventListener('openRouteLive', handler);
  }, [token]);

  // When the operator landed here from "Ver rota em tempo real" on the
  // Orders screen, we don't open the monitor — we just highlight the row
  // so the operator can click "Ver em tempo real" if they want, while
  // keeping the list overview. The flag is consumed once.
  const [highlightedRouteId, setHighlightedRouteId] = useState(() => {
    const id = Number(window.__highlightRouteId);
    if (Number.isFinite(id)) {
      window.__highlightRouteId = null;
      // Auto-clear the highlight after a few seconds so it doesn't
      // linger forever.
      setTimeout(() => setHighlightedRouteId(null), 6000);
      return id;
    }
    return null;
  });

  // Map preview pane was permanently disabled — operators preferred to
  // see only the table (more rows visible, no split). The toggle button
  // was kept hidden for now via this hard-coded `false`. Re-enable by
  // restoring the localStorage-backed initialiser if the preview is
  // wanted again.
  // hideMapPreview prop forces the preview off (drawer mode in the planner).
  const [previewVisible, setPreviewVisible] = useState(false);
  const previewVisibleEff = hideMapPreview ? false : previewVisible;
  useEffect(() => { localStorage.setItem('routesLibrary.previewVisible', String(previewVisible)); }, [previewVisible]);

  // Resizable split — percentage of the viewport used by the top pane
  const [topHeightPct, setTopHeightPct] = useState(55);
  const splitRef = useRef(null);
  const draggingRef = useRef(false);

  const onStartDrag = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';
  }, []);

  useEffect(() => {
    function onMove(e) {
      if (!draggingRef.current || !splitRef.current) return;
      const rect = splitRef.current.getBoundingClientRect();
      const pct = ((e.clientY - rect.top) / rect.height) * 100;
      const clamped = Math.max(20, Math.min(80, pct));
      setTopHeightPct(clamped);
    }
    function onUp() {
      if (draggingRef.current) {
        draggingRef.current = false;
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      }
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // Read context from Orders screen (set via window global before dispatching setActiveView)
  const [fromOrder, setFromOrder] = useState(null);
  const [driverPdfPreview, setDriverPdfPreview] = useState(null); // { route, plate, driver }

  // Load a route into the planner for editing. The operator can modify waypoints,
  // recalculate, and save — the existing route is updated (not duplicated).
  const handleEditRoute = useCallback((routeId, e) => {
    if (e) e.stopPropagation();
    if (!token || !routeId) return;
    dispatch(fetchRouteDetail({ token, routeId }));
    dispatch(setSidebarVisible(true));
    dispatch(setSidebarTab(0));
    dispatch(setShowDetails(true));
    // Flag the route as being replanned so the planner shows the
    // Save / Save & Dispatch / Cancel action bar (same UX as the
    // "Editar rota" coming from an OT). Without this flag the planner
    // would just load the route in read-only mode.
    dispatch(setReplanningRouteId(routeId));
    dispatch(navigateTo('planner'));
    // Mark context so the back button returns to the library
    window.__fromRoutesLibrary = true;
    dispatch(addToast({
      type: 'info',
      message: pt ? 'Rota carregada para edição' : 'Route loaded for editing',
    }));
  }, [dispatch, token, pt]);

  // Reabrir uma rota concluída/cancelada: o backend devolve-a a `planned`
  // (editável) e regista a reabertura para auditoria. De seguida carregamo-la
  // no planner para edição — é o propósito de reabrir.
  const handleReopenRoute = useCallback(async (routeId, e) => {
    if (e) e.stopPropagation();
    if (!token || !routeId) return;
    try {
      await dispatch(reopenRoute({ token, routeId })).unwrap();
      dispatch(addToast({
        type: 'success',
        message: pt ? 'Rota reaberta — agora editável' : 'Route reopened — now editable',
      }));
      handleEditRoute(routeId);
    } catch (err) {
      dispatch(addToast({
        type: 'error',
        message: (pt ? 'Falha ao reabrir rota: ' : 'Failed to reopen route: ') + (err || ''),
      }));
    }
  }, [dispatch, token, pt, handleEditRoute]);

  // Reutilizar: duplica a rota como nova draft (sem ligação às OTs originais)
  // e recarrega a lista para o operador a ver no topo.
  const handleDuplicateRoute = useCallback(async (routeId, e) => {
    if (e) e.stopPropagation();
    if (!token || !routeId) return;
    try {
      const copy = await dispatch(duplicateRoute({ token, routeId })).unwrap();
      dispatch(addToast({
        type: 'success',
        message: pt
          ? `Rota duplicada como nova rota #${copy.id} (draft)`
          : `Route duplicated as new route #${copy.id} (draft)`,
      }));
      fetchRoutes();
    } catch (err) {
      dispatch(addToast({
        type: 'error',
        message: (pt ? 'Falha ao duplicar rota: ' : 'Failed to duplicate route: ') + (err || ''),
      }));
    }
  }, [dispatch, token, pt]);
  useEffect(() => {
    if (window.__routesContext) {
      const ctx = window.__routesContext;
      setFromOrder(ctx.order || ctx);
      if (ctx.search) setSearch(ctx.search);
      if (ctx.period) setPeriod(ctx.period);
      if (ctx.status) setStatusFilter(ctx.status);
      window.__routesContext = null;
    }
  }, []);

  const fetchRoutes = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      // Inner fetch — separate to allow recursion when the current page has
      // become invalid after a deletion (we auto-drop to the new last page).
      const doFetch = async (requestedPage) => {
        const params = new URLSearchParams({
          page: requestedPage, limit: pageSize,
          sortBy: 'createdAt', sortDir: 'desc',
        });
        if (search.trim()) params.set('search', search.trim());
        if (statusFilter) params.set('status', statusFilter);
        const dates = periodToDates(period);
        if (dates.dateFrom) params.set('dateFrom', dates.dateFrom);
        if (dates.dateTo) params.set('dateTo', dates.dateTo);
        const res = await fetch(`${API_BASE}/routes?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return null;
        return res.json();
      };

      let data = await doFetch(page);
      if (!data) return;
      const list = data.data || [];
      const totalRows = data.pagination?.total ?? 0;
      const totalPgs = data.pagination?.totalPages ?? 1;

      // If the current page is now beyond the last page (e.g. after bulk delete),
      // fall back to the new last page and refetch — this is the invisible-list bug.
      if (list.length === 0 && totalRows > 0 && page > totalPgs) {
        const newPage = Math.max(1, totalPgs);
        const fallback = await doFetch(newPage);
        if (fallback) {
          setPage(newPage);
          setRoutes(fallback.data || []);
          setTotal(fallback.pagination?.total ?? 0);
          setTotalPages(fallback.pagination?.totalPages ?? 1);
          return;
        }
      }

      setRoutes(list);
      setTotal(totalRows);
      setTotalPages(totalPgs);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Routes fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, search, period, statusFilter]);

  useEffect(() => { fetchRoutes(); }, [fetchRoutes]);

  // Reset page when filters or page size change
  useEffect(() => { setPage(1); }, [search, period, statusFilter, pageSize]);

  const handleToggleSelect = useCallback((id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selected.length === routes.length) setSelected([]);
    else setSelected(routes.map(r => r.id));
  }, [selected.length, routes]);

  const handleClearSelection = useCallback(() => setSelected([]), []);

  // Select a route. Default behaviour shows it in the preview pane. When
  // `onSelectRoute` is provided (drawer mode in the planner), the host
  // takes over — typically to load the route into the planner.
  const handleSelectForPreview = useCallback(async (routeId) => {
    if (!token || !routeId) return;
    // Drawer mode: hand the row click off to the host.
    if (onSelectRoute) {
      const route = routes.find(r => r.id === routeId);
      onSelectRoute(route || { id: routeId, dbId: routeId });
      return;
    }
    if (previewId === routeId) return;
    setPreviewId(routeId);
    setPreviewLoading(true);
    setFromOrder(prev => {
      if (!prev) return prev;
      if (prev.assignedRouteId === routeId) return prev;
      // Different route chosen — wipe context and any search pre-filled from it
      setSearch('');
      return null;
    });
    try {
      const res = await fetch(`${API_BASE}/routes/${routeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const detail = await res.json();
        setPreviewRoute(detail);
      }
    } catch {}
    finally {
      setPreviewLoading(false);
    }
  }, [token, previewId, onSelectRoute, routes]);

  // Auto-select: prefer the route assigned to the incoming order, else first in list.
  // Skipped in drawer mode (onSelectRoute) — there the operator must click a row
  // explicitly; auto-selecting would immediately load a route and close the drawer.
  useEffect(() => {
    if (onSelectRoute) return;
    if (routes.length === 0) return;
    const assignedId = fromOrder?.assignedRouteId;
    if (!previewId) {
      const target = (assignedId && routes.find(r => r.id === assignedId)) ? assignedId : routes[0].id;
      handleSelectForPreview(target);
    }
    // Clear preview if previously-selected route is no longer in the list
    if (previewId && !routes.some(r => r.id === previewId)) {
      handleSelectForPreview(routes[0].id);
    }
  }, [routes, previewId, handleSelectForPreview, fromOrder, onSelectRoute]);

  const handleCompareOnMap = useCallback(async () => {
    if (!token || selected.length === 0) return;
    // Fetch full detail for each selected route
    const details = [];
    for (const id of selected) {
      try {
        const res = await fetch(`${API_BASE}/routes/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) details.push(await res.json());
      } catch {}
    }
    if (details.length === 0) return;
    // Show these saved routes on the planner map via the existing displayedSavedRoutes mechanism
    dispatch(setDisplayedSavedRoutes(details.map(r => ({
      id: `api-${r.id}`,
      dbId: r.id,
      name: r.name,
      origin: r.origin,
      destination: r.destination,
      computedRoute: r.routeData?.computedRoute || null,
    }))));
    dispatch(navigateTo('planner'));
    dispatch(setSidebarVisible(true));
    dispatch(addToast({
      type: 'success',
      message: `${details.length} rotas carregadas no mapa para comparação`,
    }));
  }, [dispatch, token, selected]);

  const handleDuplicate = useCallback(async () => {
    if (!token || selected.length === 0) return;
    let ok = 0;
    for (const id of selected) {
      try {
        const res = await fetch(`${API_BASE}/routes/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) continue;
        const full = await res.json();
        const duplicated = {
          ...full.routeData,
          origin: full.origin,
          destination: full.destination,
          waypoints: full.waypoints,
          name: `${full.name || 'Rota'} (cópia)`,
          dbId: null, id: null,
          status: 'draft',
        };
        await dispatch(saveRouteToAPI({ token, route: duplicated })).unwrap().then(() => { ok++; });
      } catch {}
    }
    dispatch(addToast({
      type: ok > 0 ? 'success' : 'error',
      message: ok > 0 ? `${ok} rota${ok > 1 ? 's duplicadas' : ' duplicada'}` : 'Erro ao duplicar',
    }));
    setSelected([]);
    fetchRoutes();
  }, [dispatch, token, selected, fetchRoutes]);

  const handleExportPdf = useCallback(async () => {
    if (!token || selected.length === 0) return;
    let ok = 0;
    for (const id of selected) {
      try {
        const res = await fetch(`${API_BASE}/routes/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) continue;
        const full = await res.json();
        const routeObj = {
          ...full.routeData,
          origin: full.origin,
          destination: full.destination,
          waypoints: full.waypoints,
          name: full.name,
        };
        exportRoutePdf(routeObj, full.vehicle?.plate || '', full.driver?.name || '');
        ok++;
      } catch {}
    }
    dispatch(addToast({
      type: 'success',
      message: `${ok} PDF${ok !== 1 ? 's exportados' : ' exportado'}`,
    }));
  }, [dispatch, token, selected]);

  const handleSendToVehicle = useCallback(async () => {
    if (!token || selected.length === 0) return;
    let ok = 0;
    for (const id of selected) {
      try {
        const res = await fetch(`${API_BASE}/routes/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) continue;
        const full = await res.json();
        if (!full.vehicleId) continue;
        await dispatch(sendRouteToVehicle({
          token,
          route: { dbId: full.id },
          vehicleId: full.vehicleId,
        })).unwrap().then(() => { ok++; });
      } catch {}
    }
    dispatch(addToast({
      type: ok > 0 ? 'success' : 'error',
      message: ok > 0 ? `${ok} rota${ok > 1 ? 's enviadas' : ' enviada'} ao veículo` : 'Nenhuma rota tem veículo atribuído',
    }));
    setSelected([]);
    fetchRoutes();
  }, [dispatch, token, selected, fetchRoutes]);

  // Kick off the delete flow: open the dialog in its first step (initial confirm)
  const handleDelete = useCallback(() => {
    if (!token || selected.length === 0) return;
    setDeleteDialog({ step: 'initial', linkedPreview: null });
  }, [token, selected]);

  // Actually run the delete. The API may reject twice: first asking for
  // `force` (open OTs linked), then again for `purgeHistory` (delivered or
  // cancelled OTs linked — guards against destroying history silently).
  const runDelete = useCallback(async (force, purgeHistory = false) => {
    if (!token || selected.length === 0) return;
    let ok = 0;
    let linkedHit = null;
    let linkedClosedHit = null;
    const successfullyDeleted = new Set();
    for (const id of selected) {
      try {
        const result = await dispatch(deleteRouteFromAPI({ token, routeId: id, force, purgeHistory }));
        if (result.error && result.payload?.code === 'linked-closed') {
          if (!purgeHistory && !linkedClosedHit) linkedClosedHit = result.payload;
        } else if (result.error && result.payload?.code === 'linked') {
          if (!force && !linkedHit) linkedHit = result.payload;
        } else if (!result.error) {
          ok++;
          successfullyDeleted.add(id);
        }
      } catch {}
    }

    // ── Optimistic state update — remove deleted rows immediately so the UI
    //    reflects the deletion even if the server fetch is delayed.
    if (successfullyDeleted.size > 0) {
      setRoutes(prev => prev.filter(r => !successfullyDeleted.has(r.id)));
      setTotal(prev => Math.max(0, prev - successfullyDeleted.size));
      setSelected(prev => prev.filter(id => !successfullyDeleted.has(id)));
      if (previewId && successfullyDeleted.has(previewId)) {
        setPreviewId(null);
        setPreviewRoute(null);
      }
    }

    if (linkedClosedHit && !purgeHistory) {
      // Closed-OT warning takes precedence — user must explicitly agree to
      // destroy history for delivered/cancelled OTs.
      setDeleteDialog({ step: 'linked-closed', linkedPreview: linkedClosedHit });
      return;
    }
    if (linkedHit && !force) {
      setDeleteDialog({ step: 'linked', linkedPreview: linkedHit });
      return;
    }

    setDeleteDialog(null);
    if (ok > 0) {
      dispatch(addToast({
        type: 'success',
        message: `${ok} rota${ok > 1 ? 's eliminadas' : ' eliminada'}`,
      }));
    }

    // Background refetch to reconcile with the server (handles page overflow etc.)
    // Use a small delay to let state settle — React may batch updates.
    setTimeout(() => { fetchRoutes(); }, 100);
  }, [dispatch, token, selected, fetchRoutes, previewId]);

  const toggleGroup = useCallback((key) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const grouped = useMemo(() => groupRoutes(routes, groupBy), [routes, groupBy]);

  const allSelected = routes.length > 0 && selected.length === routes.length;
  const someSelected = selected.length > 0 && selected.length < routes.length;

  function renderRow(r) {
    const status = r.status || 'draft';
    const cfg = getRouteStatusCfg(r);
    const isSelected = selected.includes(r.id);
    const isPreviewed = previewId === r.id;
    // Mantém isLive para `in_progress` (incluindo a sub-fase de regresso) —
    // o painel "live" continua a aplicar-se: ainda há GPS, ainda há ETA.
    const isLive = status === 'in_progress';
    // Resolve live state for in-progress routes. Counts come from the
    // backend's groupBy aggregation (`r.progress`), elapsed/remaining are
    // computed against `actualStartedAt` (hoisted to top-level by the
    // backend) and `totalDurationS`. GPS freshness checks the `vehicles`
    // slice — if the matched vehicle has reported within ~3 minutes we
    // show a green pulse, else amber.
    let live = null;
    if (isLive) {
      const totalOTs = r.progress?.totalOTs ?? 0;
      const deliveredOTs = r.progress?.deliveredOTs ?? 0;
      const onSiteOTs = r.progress?.onSiteOTs ?? 0;
      const pct = totalOTs > 0 ? Math.round((deliveredOTs / totalOTs) * 100) : 0;
      const startedAt = r.actualStartedAt ? new Date(r.actualStartedAt).getTime() : null;
      const elapsedSec = startedAt ? Math.max(0, Math.round((Date.now() - startedAt) / 1000)) : null;
      const totalSec = r.totalDurationS || 0;
      const remainingSec = (totalSec && elapsedSec != null) ? Math.max(0, totalSec - elapsedSec) : null;
      // GPS freshness — match by id then plate so a vehicle reattributed
      // via SQL still resolves.
      const vehId = r.vehicleId || r.vehicle?.id;
      const plate = r.vehicle?.plate || r.vehiclePlate;
      const veh = vehId
        ? vehicles.find(v => Number(v.id) === Number(vehId))
        : (plate ? vehicles.find(v => (v.plate || '').toUpperCase() === plate.toUpperCase()) : null);
      const lastFix = veh?.position?.fixTime ? new Date(veh.position.fixTime).getTime() : 0;
      const gpsAgeSec = lastFix ? Math.round((Date.now() - lastFix) / 1000) : Infinity;
      const gpsLive = gpsAgeSec < 180;
      live = { totalOTs, deliveredOTs, onSiteOTs, pct, elapsedSec, remainingSec, gpsLive, gpsAgeSec };
    }
    const isExpanded = expandedRoutes.has(r.id);
    const otRefs = Array.isArray(r.otRefs) ? r.otRefs : [];
    const isHighlightedFromOrders = highlightedRouteId === r.id;
    return (
      <React.Fragment key={r.id}>
      <Box
        className={`${classes.tableRow} ${isSelected ? classes.tableRowSelected : ''} ${isPreviewed ? classes.tableRowPreviewed : ''}`}
        style={{
          ...(gridOverride || {}),
          ...(isHighlightedFromOrders ? {
            outline: '2px solid #f59e0b',
            outlineOffset: -2,
            background: 'linear-gradient(90deg, rgba(245,158,11,0.10), rgba(245,158,11,0.04))',
            animation: 'flash-amber 1.5s ease-in-out 2',
          } : {}),
        }}
        onClick={() => handleSelectForPreview(r.id)}
      >
        {selectionEnabled && (
          <Checkbox
            size="small"
            checked={isSelected}
            onClick={e => e.stopPropagation()}
            onChange={() => handleToggleSelect(r.id)}
            style={{ padding: 2, color: isSelected ? '#6366f1' : '#cbd5e1' }}
          />
        )}
        {/* Primary label: customRef (primaryRef) — what the operator typed
            on the OT — falls back to the auto-generated route name. The
            auto-name is then shown as a small subtitle so the matrícula
            stays visible at a glance. Mirrors the SavedRoutesQuickPanel
            cards so the table and sidebar speak the same language. */}
        <Box style={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
            {/* Expand toggle — shows the route's child OTs inline. Hidden
                when the route has no OT info (legacy / failed enrich). */}
            {otRefs.length > 0 && (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); toggleExpand(r.id); }}
                style={{ padding: 0, marginRight: 2, color: '#6366f1' }}
                title={isExpanded ? (pt ? 'Recolher ordens' : 'Collapse orders') : (pt ? `Ver ${otRefs.length} ${otRefs.length === 1 ? 'ordem' : 'ordens'}` : `Show ${otRefs.length} order${otRefs.length === 1 ? '' : 's'}`)}>
                {isExpanded
                  ? <ExpandLessIcon style={{ fontSize: 16 }} />
                  : <ExpandMoreIcon style={{ fontSize: 16 }} />}
              </IconButton>
            )}
            {/* Primary line = route's auto-generated name (matrícula ·
                data · N ordens) — what the operator scans first. The
                OT customRef goes underneath as a subtitle so it stays
                visible for cross-reference without dominating the row. */}
            <Typography className={classes.routeName}>
              {r.name || `Rota #${r.id}`}
            </Typography>
            {r.vehicle?.plate && (
              <Typography className={classes.cellSub} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <LocalShippingIcon style={{ fontSize: 10 }} />{r.vehicle.plate}
              </Typography>
            )}
          </Box>
          {r.primaryRef && (
            <Typography className={classes.cellSub} style={{ fontSize: '0.58rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#475569', fontWeight: 600 }}>
              {r.primaryRef}
            </Typography>
          )}
        </Box>
        <Typography className={classes.cellText} style={{ color: r.primaryServiceDate ? '#0f766e' : '#cbd5e1', fontWeight: r.primaryServiceDate ? 600 : 400 }}>
          {r.primaryServiceDate
            ? new Date(r.primaryServiceDate).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: '2-digit' })
            : '—'}
        </Typography>
        <Typography className={classes.cellText}>
          {r.createdAt ? `${formatDate(r.createdAt)} ${new Date(r.createdAt).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}` : '—'}
        </Typography>
        {/* Execution date — when the driver actually pressed "Iniciar" on
            the app. Comes from routeData.actualStartedAt, hoisted by the
            backend list endpoint. Empty for planned/cancelled routes. */}
        <Typography className={classes.cellText} style={{ color: r.actualStartedAt ? '#16a34a' : '#cbd5e1', fontWeight: r.actualStartedAt ? 600 : 400 }}>
          {r.actualStartedAt
            ? `${formatDate(r.actualStartedAt)} ${new Date(r.actualStartedAt).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`
            : '—'}
        </Typography>
        <Typography className={classes.cellText}>
          {r.driver?.name ? (
            <><PersonIcon style={{ fontSize: 10, verticalAlign: 'middle', marginRight: 3, color: '#8b5cf6' }} />{r.driver.name}</>
          ) : <span style={{ color: '#cbd5e1' }}>—</span>}
        </Typography>
        <Box className={classes.odRow}>
          <Box className={classes.odLine}>
            <RoomIcon style={{ fontSize: 11, color: '#22c55e', flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.origin?.name || '—'}</span>
          </Box>
          <Box className={classes.odLine}>
            <FlagIcon style={{ fontSize: 11, color: '#ef4444', flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.destination?.name || '—'}</span>
          </Box>
        </Box>
        <Typography className={classes.cellText} style={{ fontWeight: 700, color: '#3b82f6' }}>
          {formatKm(r.totalDistanceM)}
        </Typography>
        <Typography className={classes.cellText} style={{ color: '#f59e0b', fontWeight: 600 }}>
          {formatDuration(r.totalDurationS)}
        </Typography>
        <Chip
          label={cfg.label}
          size="small"
          className={classes.statusBadge}
          style={{ background: cfg.bg, color: cfg.color, borderColor: `${cfg.color}60` }}
        />
        {/* Acções — Edit + Replay vivem na mesma célula do grid para a
            linha não crescer para 2 linhas quando há ambos os botões. */}
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          {/* Editar no mapa — só para rotas não-terminais. Concluídas/canceladas
              são registo histórico: em vez do lápis, oferecem Reabrir + Reutilizar. */}
          {!(r.status === 'completed' || r.status === 'cancelled') && (
            <Tooltip title={pt ? 'Editar rota no mapa' : 'Edit route on map'}>
              <IconButton
                size="small"
                onClick={(e) => handleEditRoute(r.id, e)}
                style={{
                  padding: 4, color: '#6366f1',
                  background: 'rgba(99,102,241,0.08)',
                  borderRadius: 8,
                }}
              >
                <EditIcon style={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          {/* Reabrir — devolve uma rota concluída/cancelada a estado editável,
              com auditoria. Carrega-a depois no planner para correção.
              OCULTO POR AGORA (a pedido) — mudar `false` para reativar. */}
          {false && (r.status === 'completed' || r.status === 'cancelled') && (
            <Tooltip title={pt ? 'Reabrir rota para edição' : 'Reopen route for editing'}>
              <IconButton
                size="small"
                onClick={(e) => handleReopenRoute(r.id, e)}
                style={{
                  padding: 4, color: '#d97706',
                  background: 'rgba(217,119,6,0.10)',
                  borderRadius: 8,
                }}
              >
                <LockOpenIcon style={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          {/* Reutilizar — duplica a rota como nova draft (sem ligação às OTs
              originais). OCULTO POR AGORA (a pedido) — mudar `false` para reativar. */}
          {false && (r.status === 'completed' || r.status === 'cancelled') && (
            <Tooltip title={pt ? 'Reutilizar (duplicar como nova rota)' : 'Reuse (duplicate as new route)'}>
              <IconButton
                size="small"
                onClick={(e) => handleDuplicateRoute(r.id, e)}
                style={{
                  padding: 4, color: '#6366f1',
                  background: 'rgba(99,102,241,0.08)',
                  borderRadius: 8,
                }}
              >
                <FileCopyIcon style={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          {(r.status === 'in_progress' || r.status === 'completed') && (
            <Tooltip title={pt ? 'Replay do percurso real do veículo' : 'Replay actual vehicle trail'}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setPlaybackRoute({ id: r.id, name: r.name || r.primaryRef || `Rota #${r.id}` }); }}
                style={{
                  padding: 4, color: '#16a34a',
                  background: 'rgba(22,163,74,0.10)',
                  borderRadius: 8,
                }}
              >
                <PlayArrowIcon style={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          {/* + Ordem — adiciona OT pendente. Aplica-se a draft / planned
              (Fase 1) e a in_progress (Fase 2 — backend já aceita; o
              diálogo mostra um banner âmbar a avisar que a rota está
              em trânsito e o motorista será notificado). */}
          {(r.status === 'draft' || r.status === 'planned' || r.status === 'in_progress') && (
            <Tooltip title={pt
              ? (r.status === 'in_progress' ? 'Adicionar ordem à rota EM TRÂNSITO' : 'Adicionar ordem à rota')
              : (r.status === 'in_progress' ? 'Add order to IN-TRANSIT route' : 'Add order to route')}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setAddOTRoute({
                  id: r.id,
                  name: r.name || r.primaryRef || `Rota #${r.id}`,
                  status: r.status,
                  vehicleId: r.vehicleId || null,
                  vehiclePlate: r.vehiclePlate || r.vehicle?.plate || null,
                  driverId: r.driverId || (r.routeDrivers?.[0]?.driverId) || null,
                  driverName: r.driverName || r.driver?.name || (r.routeDrivers?.[0]?.driver?.name) || null,
                  serviceDate: r.routeData?.computedRoute?.serviceDate || r.departureTime || null,
                }); }}
                style={r.status === 'in_progress' ? {
                  padding: 4, color: '#b45309',
                  background: 'rgba(245,158,11,0.14)',
                  borderRadius: 8,
                } : {
                  padding: 4, color: '#2563eb',
                  background: 'rgba(37,99,235,0.10)',
                  borderRadius: 8,
                }}
              >
                <AddIcon style={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {isLive && live && (
          <Box className={classes.liveBar} onClick={e => e.stopPropagation()}>
            {/* GPS pulse — green = fresh fix (<3min), amber = stale */}
            <Tooltip title={live.gpsLive
              ? (pt ? `GPS activo · último fix há ${Math.round(live.gpsAgeSec)}s` : `GPS active · last fix ${Math.round(live.gpsAgeSec)}s ago`)
              : (pt ? `GPS sem sinal recente${Number.isFinite(live.gpsAgeSec) ? ` (${Math.round(live.gpsAgeSec / 60)}min)` : ''}` : 'GPS stale')}>
              <Box className={classes.liveDot} style={{ background: live.gpsLive ? '#22c55e' : '#f59e0b' }} />
            </Tooltip>
            {/* Progress bar: % of OTs delivered */}
            <Box className={classes.liveProgressTrack}>
              <Box className={classes.liveProgressFill} style={{ width: `${live.pct}%` }} />
            </Box>
            <Box className={classes.liveStat}>
              <span className={classes.liveStatLabel}>{pt ? 'Ordens' : 'Orders'}</span>
              <span>{live.deliveredOTs}/{live.totalOTs}</span>
              {live.onSiteOTs > 0 && (
                <span style={{ color: '#0ea5e9', fontWeight: 700 }}>· {live.onSiteOTs} 📍</span>
              )}
            </Box>
            {live.elapsedSec != null && (
              <Box className={classes.liveStat}>
                <span className={classes.liveStatLabel}>{pt ? 'Decorrido' : 'Elapsed'}</span>
                <span>{formatDuration(live.elapsedSec)}</span>
              </Box>
            )}
            {live.remainingSec != null && (
              <Box className={classes.liveStat}>
                <span className={classes.liveStatLabel}>{pt ? 'Restante' : 'Remaining'}</span>
                <span style={{ color: '#f59e0b' }}>{formatDuration(live.remainingSec)}</span>
              </Box>
            )}
            {/* "Ver em tempo real" — abre sempre o monitor numa janela
                própria. O nome da janela é estável (`ecoLive_<routeId>`),
                por isso reabrir a mesma rota foca a janela existente
                em vez de duplicar. Botão único: o ícone "abrir em
                janela própria" foi removido — fazia o mesmo. */}
            <Tooltip title={pt
              ? 'Abrir o monitor desta rota em janela própria (foca a janela se já existir)'
              : 'Open this route monitor in its own window (focuses the existing window if any)'}>
              <Button
                size="small"
                className={classes.liveCta}
                endIcon={<OpenInNewIcon style={{ fontSize: 14 }} />}
                onClick={(e) => { e.stopPropagation(); openLiveWindow(r.id, token); }}
              >
                {pt ? 'Ver em tempo real' : 'View live'}
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>
      {/* Expanded children — OTs attached to this route. Compact list:
          number, customRef/orderNumber, delivery target, current status.
          Click any row to jump straight into that OT in the orders list. */}
      <Collapse in={isExpanded} timeout={180} unmountOnExit>
        <Box style={{
          padding: '6px 14px 10px 38px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
          borderBottom: '1px solid #e2e8f0',
          borderLeft: '3px solid #6366f1',
        }}>
          <Typography style={{ fontSize: '0.56rem', fontWeight: 800, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            {pt ? `${otRefs.length} ${otRefs.length === 1 ? 'ordem associada' : 'ordens associadas'}` : `${otRefs.length} order${otRefs.length === 1 ? '' : 's'}`}
          </Typography>
          <Box style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {otRefs.map((ot, idx) => {
              const ref = (ot.customRef || '').trim() || ot.orderNumber || `OT-${ot.id}`;
              const dest = ot.deliveryName || ot.deliveryAddress || '—';
              const stColor = ot.status === 'delivered' ? '#16a34a'
                : ot.status === 'in_transit' ? '#3b82f6'
                : ot.status === 'cancelled' ? '#dc2626'
                : '#64748b';
              const stBg = ot.status === 'delivered' ? '#dcfce7'
                : ot.status === 'in_transit' ? '#dbeafe'
                : ot.status === 'cancelled' ? '#fef2f2'
                : '#f1f5f9';
              return (
                <Box key={ot.id || idx}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '4px 10px', borderRadius: 6,
                    background: '#fff', border: '1px solid #e2e8f0',
                    fontSize: '0.66rem',
                  }}>
                  <Box style={{
                    width: 18, height: 18, borderRadius: '50%',
                    background: '#eef2ff', color: '#4338ca',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.6rem', fontWeight: 800, flexShrink: 0,
                  }}>{idx + 1}</Box>
                  <Typography style={{ fontSize: '0.7rem', fontWeight: 700, color: '#0f172a', minWidth: 96 }}>{ref}</Typography>
                  {ot.serviceType && (
                    <Typography style={{ fontSize: '0.56rem', color: '#64748b', flexShrink: 0 }}>
                      {ot.serviceType === 'assistance' ? '🔧' : ot.serviceType === 'fuel' ? '⛽' : '📦'}
                    </Typography>
                  )}
                  <Typography style={{ fontSize: '0.66rem', color: '#475569', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {dest}
                  </Typography>
                  {ot.serviceDate && (
                    <Typography style={{ fontSize: '0.58rem', color: '#0f766e', flexShrink: 0 }}>
                      {new Date(ot.serviceDate).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}
                    </Typography>
                  )}
                  <Box style={{
                    padding: '1px 7px', borderRadius: 999,
                    background: stBg, color: stColor,
                    fontSize: '0.54rem', fontWeight: 800,
                    textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0,
                  }}>{ot.status || 'pending'}</Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Collapse>
      </React.Fragment>
    );
  }

  // Live monitor takes over the entire panel when active. Returns to the
  // catalogue list with a single "Voltar" click in the monitor header.
  if (liveMonitorRouteId) {
    return (
      <LiveRouteMonitor
        routeId={liveMonitorRouteId}
        onClose={() => {
          setLiveMonitorRouteId(null);
          fetchRoutes(); // refresh counts in case the route just completed
        }}
      />
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header — hidden in embedded mode so it doesn't double up with the host's own chrome */}
      {!embedded && (
      <Box className={classes.header}>
        <IconButton size="small" onClick={() => dispatch(setActiveView(previousView || 'orders'))}>
          <ArrowBackIcon style={{ fontSize: 20 }} />
        </IconButton>
        <Box className={classes.titleBlock}>
          <Box className={classes.titleIcon}>
            <DirectionsIcon style={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Typography className={classes.title}>
            {pt ? 'Biblioteca de Rotas' : 'Routes Library'}
          </Typography>
          <Chip label={total} size="small" className={classes.totalChip} />
        </Box>
        <Box className={classes.headerActions}>
          <Tooltip title={previewVisible ? (pt ? 'Esconder preview do mapa' : 'Hide map preview') : (pt ? 'Mostrar preview do mapa' : 'Show map preview')}>
            <IconButton size="small" onClick={() => setPreviewVisible(v => !v)}
              style={{ background: previewVisible ? '#eef2ff' : 'transparent', borderRadius: 8 }}>
              {previewVisible
                ? <VisibilityIcon style={{ fontSize: 18, color: '#6366f1' }} />
                : <VisibilityOffIcon style={{ fontSize: 18, color: '#94a3b8' }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Actualizar">
            <IconButton size="small" onClick={fetchRoutes}>
              <RefreshIcon style={{ fontSize: 18, color: '#64748b' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      )}

      {/* Order context banner — shown when arriving from an OT */}
      {fromOrder && (fromOrder.orderNumber || fromOrder.plate) && (
        <Box className={classes.orderBanner}>
          <Box className={classes.orderBannerIcon}>
            <AssignmentIcon style={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Box className={classes.orderBannerContent}>
            <Typography className={classes.orderBannerLabel}>
              {pt ? 'A consultar a ordem de transporte' : 'Viewing transport order'}
            </Typography>
            <Box className={classes.orderBannerTitle}>
              <span>{fromOrder.orderNumber ? `OT ${fromOrder.orderNumber}` : 'Ordem de Transporte'}</span>
              {fromOrder.plate && <Chip size="small" label={fromOrder.plate} className={classes.orderBannerChip} />}
              {fromOrder.driver && <Chip size="small" label={fromOrder.driver} className={classes.orderBannerChip} />}
              {fromOrder.assignedRouteId && (
                <Chip size="small" label={`Rota #${fromOrder.assignedRouteId}`} className={classes.orderBannerChip}
                  style={{ background: 'rgba(255,255,255,0.35)', fontWeight: 800 }} />
              )}
            </Box>
            {(fromOrder.origin || fromOrder.destination) && (
              <Box className={classes.orderBannerMeta}>
                {fromOrder.origin && <span>📍 {fromOrder.origin.split(',')[0]}</span>}
                {fromOrder.destination && <span>→ {fromOrder.destination.split(',')[0]}</span>}
              </Box>
            )}
          </Box>
          <Box style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {previewRoute && usesDriverApp && (
              <Tooltip title={pt ? 'Pré-visualizar PDF no telemóvel do motorista' : 'Preview driver PDF on phone'}>
                <Button size="small" variant="contained"
                  onClick={() => {
                    const routeObj = {
                      ...previewRoute.routeData,
                      origin: previewRoute.origin,
                      destination: previewRoute.destination,
                      waypoints: previewRoute.waypoints,
                      name: previewRoute.name,
                    };
                    setDriverPdfPreview({
                      route: routeObj,
                      plate: previewRoute.vehicle?.plate || fromOrder.plate || '',
                      driver: previewRoute.driver?.name || fromOrder.driver || '',
                    });
                  }}
                  startIcon={<PictureAsPdfIcon style={{ fontSize: 14 }} />}
                  style={{
                    background: 'rgba(255,255,255,0.95)', color: '#4338ca',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    fontSize: '0.68rem', fontWeight: 700, textTransform: 'none',
                    borderRadius: 8, padding: '5px 14px',
                  }}>
                  {pt ? 'PDF Motorista' : 'Driver PDF'}
                </Button>
              </Tooltip>
            )}
            <Button size="small" variant="contained"
              onClick={() => setFromOrder(null)}
              style={{
                background: 'rgba(255,255,255,0.2)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.4)', boxShadow: 'none',
                fontSize: '0.65rem', fontWeight: 700, textTransform: 'none',
                borderRadius: 8, padding: '4px 12px',
              }}>
              {pt ? 'Limpar contexto' : 'Clear context'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Filters */}
      <Box className={classes.filterBar}>
        <TextField
          variant="outlined"
          size="small"
          placeholder={pt ? 'Pesquisar por nome, matrícula, motorista…' : 'Search by name, plate, driver…'}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ fontSize: 16, color: '#94a3b8' }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <ClearIcon style={{ fontSize: 14, color: '#94a3b8' }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Select
          variant="outlined"
          value={period}
          onChange={e => setPeriod(e.target.value)}
          className={classes.periodSelect}
        >
          {PERIOD_OPTIONS.map(p => (
            <MenuItem key={p.key} value={p.key} style={{ fontSize: '0.76rem' }}>{p.label}</MenuItem>
          ))}
        </Select>
        <Select
          variant="outlined"
          value={groupBy}
          onChange={e => setGroupBy(e.target.value)}
          className={classes.groupSelect}
        >
          {GROUP_OPTIONS.map(g => (
            <MenuItem key={g.key} value={g.key} style={{ fontSize: '0.76rem' }}>{g.label}</MenuItem>
          ))}
        </Select>
        <Box className={classes.statusRow}>
          {STATUS_OPTIONS.map(s => {
            const cfg = STATUS_CFG[s];
            const active = statusFilter === s;
            return (
              <Chip
                key={s}
                label={cfg.label}
                size="small"
                className={classes.statusChip}
                onClick={() => setStatusFilter(active ? '' : s)}
                style={active ? {
                  background: cfg.color, color: '#fff', borderColor: cfg.color,
                } : {
                  background: cfg.bg, color: cfg.color, borderColor: `${cfg.color}40`,
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Split: top = table, bottom = preview map (resizable, toggleable) */}
      <Box className={classes.splitContainer} ref={splitRef}>
      <Box className={classes.topPane} style={{ height: previewVisibleEff ? `${topHeightPct}%` : '100%' }}>
        {/* Bulk action bar */}
        {selectionEnabled && selected.length > 0 && (
          <Box className={classes.bulkBar}>
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={handleSelectAll}
              style={{ padding: 2, color: '#6366f1' }}
            />
            <Typography className={classes.bulkText}>
              {selected.length} rota{selected.length > 1 ? 's' : ''} seleccionada{selected.length > 1 ? 's' : ''}
            </Typography>
            {/* "Comparar no mapa" only makes sense with 2+ routes — the
                single-selection case had a button that did nothing useful
                and confused the operator. */}
            {selected.length >= 2 && (
              <Button size="small" variant="contained" className={classes.bulkBtn}
                startIcon={<CompareArrowsIcon style={{ fontSize: 14 }} />}
                onClick={handleCompareOnMap}
                style={{ background: '#6366f1', color: '#fff', boxShadow: 'none' }}>
                Comparar no mapa
              </Button>
            )}
            <Button size="small" variant="outlined" className={classes.bulkBtn}
              startIcon={<FileCopyIcon style={{ fontSize: 14 }} />}
              onClick={handleDuplicate}
              style={{ borderColor: '#3b82f6', color: '#3b82f6' }}>
              Duplicar
            </Button>
            <Button size="small" variant="outlined" className={classes.bulkBtn}
              startIcon={<PictureAsPdfIcon style={{ fontSize: 14 }} />}
              onClick={handleExportPdf}
              style={{ borderColor: '#ef4444', color: '#ef4444' }}>
              PDF
            </Button>
            <Button size="small" variant="outlined" className={classes.bulkBtn}
              startIcon={<SendIcon style={{ fontSize: 14 }} />}
              onClick={handleSendToVehicle}
              style={{ borderColor: '#22c55e', color: '#22c55e' }}>
              Enviar p/ veículo
            </Button>
            <Button size="small" variant="outlined" className={classes.bulkBtn}
              startIcon={<DeleteIcon style={{ fontSize: 14 }} />}
              onClick={handleDelete}
              style={{ borderColor: '#64748b', color: '#64748b' }}>
              Eliminar
            </Button>
            <IconButton size="small" onClick={handleClearSelection} style={{ padding: 4 }}>
              <ClearIcon style={{ fontSize: 16, color: '#64748b' }} />
            </IconButton>
          </Box>
        )}

        {/* Table */}
        <Box className={classes.tableCard}>
          {/* Table header */}
          <Box className={classes.tableHeaderRow} style={gridOverride || undefined}>
            {selectionEnabled && (
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={handleSelectAll}
                style={{ padding: 2, color: '#6366f1' }}
              />
            )}
            <Typography className={classes.th}>Nome / Veículo</Typography>
            <Typography className={classes.th}>Data Serviço</Typography>
            <Typography className={classes.th}>Criação</Typography>
            <Typography className={classes.th}>Execução</Typography>
            <Typography className={classes.th}>Motorista</Typography>
            <Typography className={classes.th}>Origem → Destino</Typography>
            <Typography className={classes.th}>Km</Typography>
            <Typography className={classes.th}>Duração</Typography>
            <Typography className={classes.th}>Status</Typography>
            <Typography className={classes.th} style={{ textAlign: 'center' }}>Ações</Typography>
          </Box>

          {/* Body */}
          {loading ? (
            <Box className={classes.loadingBox}>
              <CircularProgress size={28} style={{ color: '#6366f1' }} />
            </Box>
          ) : routes.length === 0 ? (
            <Box className={classes.emptyState}>
              <FolderOpenIcon style={{ fontSize: 42, color: '#cbd5e1' }} />
              <Typography style={{ fontSize: '0.82rem', fontWeight: 500 }}>
                Nenhuma rota encontrada
              </Typography>
              <Typography style={{ fontSize: '0.7rem' }}>
                Tente alterar os filtros de pesquisa
              </Typography>
            </Box>
          ) : (
            grouped.map(group => {
              const isCollapsed = collapsedGroups.has(group.key);
              return (
                <React.Fragment key={group.key}>
                  {group.label && (
                    <Box className={classes.groupHeader} onClick={() => toggleGroup(group.key)}>
                      {isCollapsed ? <ExpandMoreIcon style={{ fontSize: 18, color: '#6366f1' }} /> : <ExpandLessIcon style={{ fontSize: 18, color: '#6366f1' }} />}
                      <Typography className={classes.groupLabel}>{group.label}</Typography>
                      <Typography className={classes.groupCount}>{group.items.length}</Typography>
                    </Box>
                  )}
                  <Collapse in={!isCollapsed} timeout={200}>
                    {group.items.map(renderRow)}
                  </Collapse>
                </React.Fragment>
              );
            })
          )}
        </Box>

      {/* Pagination — professional Material-style footer with page-size
          selector, range counter, jump-to-first/last and prev/next.
          Designed to scale to hundreds of routes without overflowing. */}
      {(total > 0 || totalPages > 1) && (
        <Box style={{
          display: 'flex', alignItems: 'center', gap: 16,
          padding: '10px 16px', marginTop: 8,
          background: '#fff', borderTop: '1px solid #e2e8f0',
          borderRadius: '0 0 8px 8px',
          flexShrink: 0,
        }}>
          {/* Range counter — "1–25 de 234" style */}
          <Typography style={{ fontSize: '0.74rem', color: '#475569', fontWeight: 600 }}>
            {(() => {
              const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
              const to = Math.min(page * pageSize, total);
              return pt
                ? `${from}–${to} de ${total}`
                : `${from}–${to} of ${total}`;
            })()}
          </Typography>

          {/* Page size selector */}
          <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Typography style={{ fontSize: '0.7rem', color: '#64748b' }}>
              {pt ? 'Por página:' : 'Per page:'}
            </Typography>
            <Select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              variant="outlined"
              style={{ fontSize: '0.74rem', height: 28, minWidth: 60 }}
              MenuProps={{ PaperProps: { style: { borderRadius: 8 } } }}
            >
              {PAGE_LIMIT_OPTIONS.map(n => (
                <MenuItem key={n} value={n} style={{ fontSize: '0.74rem' }}>{n}</MenuItem>
              ))}
            </Select>
          </Box>

          <Box style={{ flex: 1 }} />

          {/* Page navigation */}
          <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Tooltip title={pt ? 'Primeira página' : 'First page'}>
              <span>
                <IconButton size="small" disabled={page <= 1} onClick={() => setPage(1)}
                  style={{ padding: 4, color: page <= 1 ? '#cbd5e1' : '#475569' }}>
                  <span style={{ fontSize: 14, fontWeight: 800 }}>«</span>
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={pt ? 'Anterior' : 'Previous'}>
              <span>
                <IconButton size="small" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
                  style={{ padding: 4, color: page <= 1 ? '#cbd5e1' : '#475569' }}>
                  <ChevronLeftIcon style={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Typography style={{ fontSize: '0.74rem', color: '#0f172a', fontWeight: 700, padding: '0 8px', minWidth: 80, textAlign: 'center' }}>
              {pt ? `Pág. ${page} / ${totalPages}` : `Page ${page} / ${totalPages}`}
            </Typography>
            <Tooltip title={pt ? 'Seguinte' : 'Next'}>
              <span>
                <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  style={{ padding: 4, color: page >= totalPages ? '#cbd5e1' : '#475569' }}>
                  <ChevronRightIcon style={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={pt ? 'Última página' : 'Last page'}>
              <span>
                <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage(totalPages)}
                  style={{ padding: 4, color: page >= totalPages ? '#cbd5e1' : '#475569' }}>
                  <span style={{ fontSize: 14, fontWeight: 800 }}>»</span>
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}
      </Box>{/* end topPane */}

      {/* Resize handle (drag to reshape) — only visible when preview is shown */}
      {previewVisibleEff && (
        <Box className={classes.resizeHandle} onMouseDown={onStartDrag}
          title={pt ? 'Arrastar para redimensionar' : 'Drag to resize'}>
          <Box className={classes.resizeGrip}>
            <DragHandleIcon style={{ fontSize: 0 }} />
          </Box>
        </Box>
      )}

      {/* Bottom pane — minimal route preview map (floating card) — hidden when previewVisible=false */}
      {previewVisibleEff && (
        <Box className={classes.bottomPane} style={{ height: `calc(${100 - topHeightPct}% - 10px)` }}>
          <Box className={classes.mapFloatingCard}>
            <RoutePreviewMap route={previewRoute} loading={previewLoading} />
          </Box>
        </Box>
      )}
      </Box>{/* end splitContainer */}

      {/* Delete confirmation dialog — 2-step: initial confirm → linked OTs warning if needed */}
      {deleteDialog && (
        <Box style={{
          position: 'fixed', inset: 0, zIndex: 1500,
          background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setDeleteDialog(null)}>
          <Box style={{
            width: 520, maxWidth: '94vw', maxHeight: '88vh',
            background: '#fff', borderRadius: 14, overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
          }} onClick={(e) => e.stopPropagation()}>
            {(() => {
              const isClosed = deleteDialog.step === 'linked-closed';
              const isLinked = deleteDialog.step === 'linked';
              const headerBg = isClosed
                ? 'linear-gradient(135deg, #fee2e2, #fecaca)'
                : isLinked
                  ? 'linear-gradient(135deg, #fef3c7, #fde68a)'
                  : 'linear-gradient(135deg, #fef2f2, #fecaca)';
              const headerBorder = isClosed ? '#ef4444' : isLinked ? '#fbbf24' : '#f87171';
              const iconBg = isClosed || !isLinked
                ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                : 'linear-gradient(135deg, #f59e0b, #d97706)';
              const iconGlyph = isClosed ? '⚠' : isLinked ? '⚠' : '🗑';
              const title = isClosed
                ? 'Rota com ordens concluídas / canceladas'
                : isLinked
                  ? 'Rotas associadas a ordens de transporte'
                  : `Eliminar ${selected.length} rota${selected.length > 1 ? 's' : ''}?`;
              const subtitle = isClosed
                ? 'Apagar esta rota vai destruir o histórico das ordens'
                : isLinked
                  ? 'É necessário confirmar a desassociação das ordens'
                  : 'Esta acção é irreversível';
              const buttonLabel = isClosed
                ? 'Apagar histórico e eliminar'
                : isLinked
                  ? 'Desassociar e eliminar'
                  : 'Eliminar definitivamente';
              return (
                <>
                  {/* Header */}
                  <Box style={{
                    padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12,
                    background: headerBg,
                    borderBottom: `1px solid ${headerBorder}`,
                  }}>
                    <Box style={{
                      width: 42, height: 42, borderRadius: 11,
                      background: iconBg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      fontSize: 22,
                    }}>{iconGlyph}</Box>
                    <Box style={{ flex: 1 }}>
                      <Typography style={{ fontSize: '0.98rem', fontWeight: 800, color: '#1e293b' }}>{title}</Typography>
                      <Typography style={{ fontSize: '0.68rem', color: '#64748b' }}>{subtitle}</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setDeleteDialog(null)}>
                      <ClearIcon style={{ fontSize: 20, color: '#64748b' }} />
                    </IconButton>
                  </Box>

                  {/* Body */}
                  <Box style={{ padding: '18px 22px', overflow: 'auto', flex: 1 }}>
                    {deleteDialog.step === 'initial' ? (
                      <Typography style={{ fontSize: '0.82rem', color: '#334155', lineHeight: 1.55 }}>
                        Tens a certeza que queres eliminar <b>{selected.length}</b> rota{selected.length > 1 ? 's' : ''} seleccionada{selected.length > 1 ? 's' : ''}? Esta operação <b>não pode ser desfeita</b>.
                      </Typography>
                    ) : (
                      <>
                        <Typography style={{ fontSize: '0.82rem', color: '#334155', marginBottom: 12, lineHeight: 1.55 }}>
                          {deleteDialog.linkedPreview?.message || 'Uma ou mais rotas seleccionadas estão associadas a ordens de transporte.'}
                        </Typography>
                        {Array.isArray(deleteDialog.linkedPreview?.linkedOTs) && deleteDialog.linkedPreview.linkedOTs.length > 0 && (
                          <Box style={{
                            maxHeight: 220, overflow: 'auto',
                            background: '#fafbfc', border: '1px solid #e2e8f0',
                            borderRadius: 8, padding: 10, marginBottom: 12,
                          }}>
                            <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                              Ordens afectadas (primeira rota):
                            </Typography>
                            {deleteDialog.linkedPreview.linkedOTs.map((ot, i) => (
                              <Box key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 4px', borderBottom: i < deleteDialog.linkedPreview.linkedOTs.length - 1 ? '1px dashed #e2e8f0' : 'none' }}>
                                <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0f172a', minWidth: 80 }}>OT {ot.orderNumber || ot.id}</Typography>
                                {ot.vehiclePlate && <Chip size="small" label={ot.vehiclePlate} style={{ height: 18, fontSize: '0.58rem', background: '#eff6ff', color: '#2563eb', fontWeight: 700 }} />}
                                {ot.driverName && <Typography style={{ fontSize: '0.64rem', color: '#64748b' }}>👤 {ot.driverName}</Typography>}
                                <Box style={{ flex: 1 }} />
                                <Chip size="small" label={STATUS_CFG[ot.status]?.label || ot.status} style={{ height: 18, fontSize: '0.58rem', background: (STATUS_CFG[ot.status]?.bg) || '#f1f5f9', color: (STATUS_CFG[ot.status]?.color) || '#64748b', fontWeight: 700 }} />
                              </Box>
                            ))}
                          </Box>
                        )}
                        {isClosed ? (
                          <Typography style={{ fontSize: '0.76rem', color: '#991b1b', padding: '8px 10px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8 }}>
                            Estas ordens já estão <b>concluídas / canceladas</b>. Apagar a rota faz desaparecer o registo geográfico da entrega e não pode ser revertido.
                          </Typography>
                        ) : (
                          <Typography style={{ fontSize: '0.76rem', color: '#92400e', padding: '8px 10px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8 }}>
                            Ao continuar, as OTs ficarão <b>desassociadas</b> (campo "Rota" vazio) e as que estavam em estado "Atribuída" voltam para <b>"Pendente"</b>.
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>

                  {/* Footer */}
                  <Box style={{ display: 'flex', gap: 8, padding: '14px 22px', borderTop: '1px solid #e2e8f0', background: '#fafbfc' }}>
                    <Button onClick={() => setDeleteDialog(null)}
                      style={{ fontSize: '0.78rem', textTransform: 'none', color: '#64748b', borderRadius: 8, padding: '8px 18px' }}>
                      Cancelar
                    </Button>
                    <Box style={{ flex: 1 }} />
                    <Button variant="contained"
                      onClick={() => runDelete(isLinked || isClosed, isClosed)}
                      startIcon={<DeleteIcon style={{ fontSize: 16 }} />}
                      style={{
                        fontSize: '0.78rem', fontWeight: 800, textTransform: 'none',
                        background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                        color: '#fff', borderRadius: 8, padding: '8px 22px',
                        boxShadow: '0 4px 12px rgba(239,68,68,0.35)',
                      }}>
                      {buttonLabel}
                    </Button>
                  </Box>
                </>
              );
            })()}
          </Box>
        </Box>
      )}

      {/* Driver PDF preview — Android phone mockup */}
      <DriverPdfPreviewDialog
        open={!!driverPdfPreview}
        route={driverPdfPreview?.route}
        vehiclePlate={driverPdfPreview?.plate}
        driverName={driverPdfPreview?.driver}
        lang={lang}
        onClose={() => setDriverPdfPreview(null)}
      />

      {/* Replay dialog — animated playback of the actual GPS trail. */}
      <RoutePlaybackDialog
        open={!!playbackRoute}
        routeId={playbackRoute?.id}
        routeName={playbackRoute?.name}
        onClose={() => setPlaybackRoute(null)}
      />

      {/* Add-OT-to-route dialog — FASE 1 (draft). */}
      <AddOTToRouteDialog
        open={!!addOTRoute}
        routeId={addOTRoute?.id}
        routeName={addOTRoute?.name}
        routeStatus={addOTRoute?.status}
        routeVehicleId={addOTRoute?.vehicleId}
        routeVehiclePlate={addOTRoute?.vehiclePlate}
        routeDriverId={addOTRoute?.driverId}
        routeDriverName={addOTRoute?.driverName}
        routeServiceDate={addOTRoute?.serviceDate}
        onClose={() => setAddOTRoute(null)}
        onAdded={() => fetchRoutes()}
      />
    </Box>
  );
}

export default RoutesView;
